import { FaBan, FaCheck } from 'react-icons/fa';
import { STATUS_META, WORKFLOW_STAGES } from '../constants';
import type { PolicyStatus } from '../types';
import { cn } from '../../../utils/cn';

type StageState = 'done' | 'current' | 'upcoming' | 'cancelled';

export const WorkflowStepper: React.FC<{ status: PolicyStatus }> = ({ status }) => {
  const currentIndex = STATUS_META[status].stageIndex;

  const stageState = (index: number): StageState => {
    if (status === 'cancelled') {
      if (index < currentIndex) return 'done';
      return index === currentIndex ? 'cancelled' : 'upcoming';
    }
    if (index < currentIndex) return 'done';
    // `issued` shares the Issuance stage with `approved`, but the stage's work is complete.
    if (index === currentIndex)
      return status === 'active' || status === 'renewed' || status === 'issued' ? 'done' : 'current';
    return 'upcoming';
  };

  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-1">
      {WORKFLOW_STAGES.map((stage, index) => {
        const state = stageState(index);
        const isLast = index === WORKFLOW_STAGES.length - 1;
        const circleStyle =
          state === 'done'
            ? { backgroundColor: 'var(--color-primary-500)', color: '#fff', borderColor: 'var(--color-primary-500)' }
            : state === 'current'
              ? { backgroundColor: 'var(--color-background-paper)', color: 'var(--color-primary-600)', borderColor: 'var(--color-primary-500)' }
              : state === 'cancelled'
                ? { backgroundColor: 'var(--color-error-500)', color: '#fff', borderColor: 'var(--color-error-500)' }
                : { backgroundColor: 'var(--color-background-paper)', color: 'var(--color-text-secondary)', borderColor: 'var(--color-border-medium)' };
        return (
          <div key={stage.key} className="flex min-w-0 items-center gap-2">
            <div className="flex flex-col items-center gap-1.5">
              <span
                className={cn('flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-bold', state === 'current' && 'ring-4 ring-[color-mix(in_srgb,var(--color-primary-500)_20%,transparent)]')}
                style={circleStyle}
              >
                {state === 'done' ? <FaCheck className="h-3 w-3" /> : state === 'cancelled' ? <FaBan className="h-3 w-3" /> : index + 1}
              </span>
              <span
                className="whitespace-nowrap text-[11px] font-semibold"
                style={{ color: state === 'upcoming' ? 'var(--color-text-secondary)' : 'var(--color-text-primary)' }}
              >
                {stage.label}
              </span>
            </div>
            {!isLast && (
              <span
                className="mb-5 h-0.5 w-8 flex-shrink-0 rounded-full sm:w-12"
                style={{ backgroundColor: state === 'done' ? 'var(--color-primary-500)' : 'var(--color-border-light)' }}
                aria-hidden="true"
              />
            )}
          </div>
        );
      })}
      {status === 'renewed' && (
        <span className="mb-5 ml-1 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold" style={{ backgroundColor: 'var(--color-hover-background)', color: 'var(--color-text-secondary)' }}>
          → Renewed
        </span>
      )}
    </div>
  );
};
